import {
  AlertTriangle,
  Download,
  Filter,
  Info,
  Loader2,
  RefreshCw,
  Undo2,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { AuraTelemetryEventLog, ProoflineStatus } from "@auraone/aura-ide-kit";
import {
  toAuraTelemetryEvents,
  type LocalDiagnosticEntry,
} from "./platformContracts";
import type {
  AppNotice,
  DatasetTab,
  SensorStream,
  UpdateState,
} from "./types";

export const appVersion = "0.2.0";

export function RoboticsStudioLogo({
  size = "standard",
}: {
  size?: "standard" | "large";
}) {
  return (
    <span
      className={`rs-logo ${size}`}
      role="img"
      aria-label="AuraOne Robotics Studio Open"
    >
      <svg className="rs-logo-symbol" viewBox="0 0 72 72" aria-hidden="true">
        <path className="rs-logo-fold" d="M12 14h21l9 9v35H12V14Zm21 0v10h10" />
        <path
          className="rs-logo-arm"
          d="M43 34h10l7 7-7 7H43m-8-8h18M27 34v14m-6-7h12"
        />
      </svg>
      <span className="rs-logo-copy">
        <small>AuraOne Open Studio</small>
        <strong>Robotics Studio</strong>
      </span>
    </span>
  );
}

export function LoadingIndex({ dataset }: { dataset: DatasetTab }) {
  return (
    <section className="surface centered" aria-live="polite">
      <Loader2 className="spin" size={32} />
      <h1>Indexing {dataset.name}</h1>
      <p>
        Validated {dataset.indexed.toLocaleString()} of{" "}
        {dataset.totalEstimate.toLocaleString()} metadata records in browser
        memory. No binary adapter or local database is active.
      </p>
      <progress value={dataset.indexed} max={dataset.totalEstimate} />
    </section>
  );
}

export function StateCard({
  icon: Icon,
  title,
  text,
}: {
  icon: typeof AlertTriangle;
  title: string;
  text: string;
}) {
  return (
    <section className="surface centered">
      <Icon size={34} />
      <h1>{title}</h1>
      <p>{text}</p>
    </section>
  );
}

export function StateLine({
  label,
  status,
  detail,
}: {
  label: string;
  status: SensorStream["status"];
  detail: string;
}) {
  const tone =
    status === "pass"
      ? "success"
      : status === "fail"
        ? "danger"
        : status === "warn"
          ? "warning"
          : "info";
  const statusLabel =
    status === "pass"
      ? "Pass"
      : status === "fail"
        ? "Failed"
        : status === "warn"
          ? "Review"
          : "Unknown";
  return (
    <article className="state-line">
      <ProoflineStatus tone={tone} label={statusLabel} />
      <strong>{label}</strong>
      <span>{detail}</span>
    </article>
  );
}

export function LocalDiagnosticEventLog({
  entries,
}: {
  entries: LocalDiagnosticEntry[];
}) {
  return (
    <section className="diagnostic-log" aria-labelledby="diagnostic-log-title">
      <div className="diagnostic-log-heading">
        <h2 id="diagnostic-log-title">Local diagnostic event preview</h2>
        <ProoflineStatus
          tone="info"
          label={`${entries.length} retained in memory`}
        />
      </div>
      <p>
        No delivery endpoint is configured, and these preview records are not
        sent by this source build.
      </p>
      <AuraTelemetryEventLog events={toAuraTelemetryEvents(entries)} />
      {entries.length === 0 ? (
        <p className="muted">
          No local diagnostic previews have been retained in this session.
        </p>
      ) : null}
    </section>
  );
}

export function DatasetProvenanceNote({
  dataset,
  context,
}: {
  dataset: DatasetTab;
  context: string;
}) {
  const synthetic = dataset.provenance.kind === "repository-synthetic-fixture";
  return (
    <div
      className={`sample-provenance ${synthetic ? "synthetic" : "imported"}`}
      role="note"
      aria-label="Dataset provenance"
    >
      <Info size={16} aria-hidden="true" />
      <strong>
        {synthetic ? "Synthetic sample" : dataset.provenance.label}
      </strong>
      <span>
        {dataset.provenance.recordCount} records
        {dataset.provenance.seedSceneCount
          ? ` from ${dataset.provenance.seedSceneCount} seed scenes`
          : ""}
        . {context}
      </span>
    </div>
  );
}

export function AppToast({ notice, close }: { notice: AppNotice; close: () => void }) {
  const action = notice.onAction;
  return (
    <div
      className={`app-toast ${notice.tone}`}
      role={notice.tone === "danger" ? "alert" : "status"}
      aria-live="polite"
    >
      <span>{notice.message}</span>
      {action && notice.actionLabel ? (
        <button
          type="button"
          onClick={() => {
            close();
            action();
          }}
        >
          <Undo2 size={15} />
          {notice.actionLabel}
        </button>
      ) : null}
      <button
        type="button"
        className="icon-button"
        onClick={close}
        aria-label="Dismiss notification"
      >
        <X size={15} />
      </button>
    </div>
  );
}

export function useDialogFocusTrap(onClose: () => void) {
  const dialogRef = useRef<HTMLElement>(null);
  const closeRef = useRef(onClose);

  useEffect(() => {
    closeRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    const focusableSelector = [
      "a[href]",
      "button:not([disabled])",
      "input:not([disabled]):not([type='hidden'])",
      "select:not([disabled])",
      "textarea:not([disabled])",
      "summary",
      "[tabindex]:not([tabindex='-1'])",
      "[contenteditable='true']",
    ].join(",");
    const focusableElements = () =>
      Array.from(
        dialog.querySelectorAll<HTMLElement>(focusableSelector),
      ).filter(
        (element) => !element.hidden && element.getClientRects().length > 0,
      );
    const initial =
      dialog.querySelector<HTMLElement>("[data-autofocus]") ??
      focusableElements()[0] ??
      dialog;
    const frame = window.requestAnimationFrame(() => initial.focus());

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        closeRef.current();
        return;
      }
      if (event.key !== "Tab") return;

      const focusable = focusableElements();
      if (!focusable.length) {
        event.preventDefault();
        dialog.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !dialog.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (
        !event.shiftKey &&
        (active === last || !dialog.contains(active))
      ) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, []);

  return dialogRef;
}

export function PanelTitle({
  icon: Icon,
  label,
}: {
  icon: typeof Filter;
  label: string;
}) {
  return (
    <h2 className="panel-title">
      <Icon size={15} /> {label}
    </h2>
  );
}

export function Metric({
  label,
  value,
  tone = "neutral",
}: {
  label: string;
  value: string;
  tone?: "neutral" | "pass" | "warn" | "fail";
}) {
  return (
    <div className={`metric ${tone}`}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

export function Chip({
  label,
  tone = "neutral",
}: {
  label: string;
  tone?: "neutral" | "pass" | "warn" | "fail";
}) {
  return <span className={`chip ${tone}`}>{label}</span>;
}

export function Sparkline({
  values,
  accentKey,
  fallbackIndex,
}: {
  values: number[];
  accentKey: SensorStream["kind"];
  fallbackIndex: number;
}) {
  const max = Math.max(...values, 1);
  const accent = sensorAccentVar(accentKey, fallbackIndex);
  return (
    <div className="sparkline" role="img" aria-label="Sensor time-series plot">
      {values.map((value, index) => (
        <span
          key={index}
          style={{
            height: `${Math.max(8, (value / max) * 100)}%`,
            background: accent,
          }}
        />
      ))}
    </div>
  );
}

export function humanTaskTitle(task: string | null) {
  if (!task) return "Task unknown";
  return task
    .replace(/_v\d+$/i, "")
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export function SyntheticSensorScene({
  sensorId,
  compact = false,
}: {
  sensorId: string;
  compact?: boolean;
}) {
  const isWrist = sensorId.includes("wrist");
  const isDepth = sensorId.includes("depth");
  const label = isDepth
    ? "Synthetic depth view of robot grasping a cup"
    : isWrist
      ? "Synthetic wrist camera view of gripper and cup"
      : "Synthetic front camera view of robot workcell";
  const source = isWrist
    ? "/media/synthetic-workcell-wrist-v1.webp"
    : "/media/synthetic-workcell-front-v1.webp";
  const mode = isDepth ? "depth" : isWrist ? "wrist" : "front";
  const channelLabel = isDepth
    ? "Depth overhead"
    : isWrist
      ? "RGB wrist"
      : "RGB front";

  return (
    <div
      className={`synthetic-scene synthetic-scene--${mode}${compact ? " synthetic-scene--compact" : ""}`}
      role={compact ? undefined : "img"}
      aria-label={compact ? undefined : label}
      aria-hidden={compact ? "true" : undefined}
      data-synthetic-source="repository-generated-media"
    >
      <img
        className="synthetic-scene__image"
        src={source}
        alt=""
        loading="eager"
        decoding="async"
        draggable={false}
      />
      {isDepth ? (
        <span className="synthetic-scene__depth-grid" aria-hidden="true">
          <i />
          <i />
          <i />
          <i />
        </span>
      ) : null}
      {!compact ? (
        <>
          <span className="synthetic-scene__channel">
            <i aria-hidden="true" />
            {channelLabel}
          </span>
          <span className="synthetic-scene__crosshair" aria-hidden="true" />
          <span className="synthetic-scene__provenance">
            <strong>Synthetic media</strong>
            <span>Repository fixture</span>
          </span>
        </>
      ) : null}
    </div>
  );
}

export function ManifestMetadataScene({
  sensor,
  compact = false,
}: {
  sensor?: SensorStream;
  compact?: boolean;
}) {
  const label = sensor?.label ?? "No visual sensor declared";
  const rate =
    sensor?.rateHz === null || sensor === undefined
      ? "Rate unknown"
      : `${sensor.rateHz} Hz`;
  const accessibleLabel = sensor
    ? `Imported manifest metadata for ${sensor.label}; no media evidence was parsed`
    : "Imported manifest contains no visual media evidence";
  return (
    <svg
      className="synthetic-scene manifest-metadata-scene"
      viewBox="0 0 960 540"
      role={compact ? undefined : "img"}
      aria-label={compact ? undefined : accessibleLabel}
      aria-hidden={compact ? "true" : undefined}
      preserveAspectRatio="xMidYMid slice"
    >
      <rect width="960" height="540" fill="#101820" />
      <path
        d="M0 90h960M0 180h960M0 270h960M0 360h960M0 450h960"
        stroke="#526572"
        strokeOpacity="0.28"
      />
      <path
        d="M160 0v540M320 0v540M480 0v540M640 0v540M800 0v540"
        stroke="#526572"
        strokeOpacity="0.22"
      />
      <rect
        x="210"
        y="90"
        width="540"
        height="360"
        rx="6"
        fill="#18242d"
        stroke="#7d8d98"
        strokeWidth="3"
      />
      <path
        d="M300 166h155l48 48v160H300Z"
        fill="#eef2f6"
        stroke="#101820"
        strokeWidth="5"
      />
      <path d="M455 166v50h50" fill="none" stroke="#101820" strokeWidth="5" />
      <path
        d="M350 272h102M350 306h102M350 340h72"
        stroke="#007582"
        strokeWidth="9"
        strokeLinecap="round"
      />
      {!compact ? (
        <g>
          <text
            x="540"
            y="205"
            fill="#ffffff"
            fontFamily="var(--pl-font-ui)"
            fontSize="22"
            fontWeight="700"
          >
            Manifest metadata
          </text>
          <text
            x="540"
            y="252"
            fill="#aebbc5"
            fontFamily="var(--pl-font-ui)"
            fontSize="18"
          >
            No media evidence parsed
          </text>
          <text
            x="540"
            y="306"
            fill="#ffffff"
            fontFamily="var(--pl-font-ui)"
            fontSize="17"
          >
            {label}
          </text>
          <text
            x="540"
            y="340"
            fill="#7dcfd4"
            fontFamily="var(--pl-font-mono)"
            fontSize="16"
          >
            {rate}
          </text>
          <rect
            x="20"
            y="20"
            width="245"
            height="34"
            rx="4"
            fill="#101820"
            stroke="#526572"
          />
          <circle cx="39" cy="37" r="6" fill="#e5ad42" />
          <text
            x="55"
            y="42"
            fill="#ffffff"
            fontFamily="var(--pl-font-ui)"
            fontSize="16"
            fontWeight="700"
          >
            Imported metadata only
          </text>
        </g>
      ) : null}
    </svg>
  );
}

export function sensorAccentVar(kind: SensorStream["kind"], fallbackIndex: number) {
  const variables: Record<SensorStream["kind"], string> = {
    rgb: "var(--sensor-rgb)",
    depth: "var(--sensor-depth)",
    joint: "var(--sensor-joint)",
    pose: "var(--sensor-pose)",
    force: "var(--sensor-force)",
    audio: "var(--sensor-audio)",
    language: "var(--sensor-language)",
    custom: "var(--sensor-custom)",
  };

  return variables[kind] ?? `var(--accent-${(fallbackIndex % 4) + 1})`;
}

export function DatasetStatus({
  status,
  compact = false,
}: {
  status: DatasetTab["status"];
  compact?: boolean;
}) {
  const tone =
    status === "ready"
      ? "success"
      : status === "loading"
        ? "review"
        : status === "error"
          ? "danger"
          : "blocked";
  const label =
    status === "ready"
      ? "Ready"
      : status === "loading"
        ? "Indexing"
        : status === "error"
          ? "Error"
          : "Empty";
  return (
    <span className={compact ? "compact-status" : ""}>
      <ProoflineStatus tone={tone} label={label} />
    </span>
  );
}

export function UpdateCenter({
  state,
  setState,
}: {
  state: UpdateState;
  setState: (state: UpdateState) => void;
}) {
  const content: Record<
    UpdateState,
    {
      tone: "success" | "info" | "review" | "warning" | "danger" | "blocked";
      title: string;
      detail: string;
      action?: string;
    }
  > = {
    checking: {
      tone: "review",
      title: "Checking update configuration",
      detail:
        "Looking for a signed release channel without asserting a published build.",
    },
    current: {
      tone: "blocked",
      title: "Published release status unavailable",
      detail: `Version ${appVersion} is running from source; no stable publication is asserted.`,
      action: "Recheck configuration",
    },
    available: {
      tone: "blocked",
      title: "Release metadata unavailable",
      detail:
        "This unpublished source build cannot verify that an update exists.",
    },
    downloading: {
      tone: "blocked",
      title: "Download unavailable",
      detail: "No signed update package is configured for this source build.",
    },
    ready: {
      tone: "blocked",
      title: "Restart update unavailable",
      detail: "No verified update has been downloaded by this source build.",
    },
    failed: {
      tone: "danger",
      title: "Update configuration check failed",
      detail:
        "The source build could not verify a signed release channel. Local work is unaffected.",
      action: "Try again",
    },
    unsupported: {
      tone: "blocked",
      title: "Release channel unavailable",
      detail:
        "Version 0.2.0 is an unpublished source build and is not connected to a signed update channel.",
    },
    "signature-invalid": {
      tone: "danger",
      title: "Signature verification failed",
      detail:
        "No installation was attempted because release verification did not pass.",
    },
  };
  const current = content[state];
  function advance() {
    if (state === "current" || state === "failed") {
      setState("checking");
      window.setTimeout(() => setState("unsupported"), 650);
    }
  }
  return (
    <section
      className="update-center"
      aria-label="Software update"
      aria-live="polite"
    >
      <div>
        <ProoflineStatus tone={current.tone} label={current.title} />
        <p>{current.detail}</p>
      </div>
      {current.action ? (
        <button onClick={advance}>
          <RefreshCw size={15} /> {current.action}
        </button>
      ) : null}
    </section>
  );
}

export function clampIndex(index: number, length: number) {
  if (length <= 0) return 0;
  return Math.min(length - 1, Math.max(0, index));
}

export function useMediaQuery(query: string) {
  const [matches, setMatches] = useState(() =>
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia(query).matches
      : false,
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia(query);
    const update = () => setMatches(mediaQuery.matches);
    update();
    mediaQuery.addEventListener("change", update);
    return () => mediaQuery.removeEventListener("change", update);
  }, [query]);

  return matches;
}